import { Dispatch, SetStateAction, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { ChevronDown } from '@/assets/icons/ChevronDown';
import { Button } from '@/components/ButtonComponent';
import { NumberPad } from '@/components/CustomNumberPad';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { textVariants } from '@/components/lib/typography';
import { AmountInput } from '@/feature/betslip-pickem/components/amount-input/AmountInput';
import { useFantasyDecimalsEnabled } from '@/feature/betslip-pickem/hooks/use-fantasy-decimals-enabled';
import { formatBalance } from '@/feature/transactions/utils/formatters';
import { Crossfade, FadeInCollapse } from '@/hooks/animated-transitions';
import { GameMode } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { QuickAmountsButtons } from './QuickAmountsButtons';
import { RaceToPlaceAmountState } from './hooks/use-race-to-place-amount-state';
import {
    HEIGHT_DURATION,
    INPUT_HEIGHT,
    QUICK_AMOUNTS_HEIGHT,
    RaceToPlaceAmountSelectorType,
} from './race-to-place-constants';

type Props = {
    selector: RaceToPlaceAmountSelectorType;
    setSelector: Dispatch<SetStateAction<RaceToPlaceAmountSelectorType>>;
    onNumberPress: (number: number) => void;
    onDecimalPress: () => void;
    onBackspacePress: () => void;
    setInputValue: (value: string) => void;
    onPlaceEntry: () => void;
    displayValue: string;
    minEntryAmount: number;
    maxEntryAmount: number;
    boostedMultiplier: number;
    errorMessage?: string;
    amountState: RaceToPlaceAmountState;
    amount: number;
    isLoading: boolean;
};

export const RaceToPlaceAmountSelectionAndSubmit = memo(
    ({
        displayValue,
        minEntryAmount,
        maxEntryAmount,
        boostedMultiplier,
        errorMessage,
        selector,
        setSelector,
        onBackspacePress,
        onNumberPress,
        onDecimalPress,
        setInputValue,
        onPlaceEntry,
        amountState,
        amount,
        isLoading,
    }: Props) => {
        const { visibility, heights } = amountState;
        const { showErrorOrSpacer, showToWin, showDepositButton, showSelectorToButtonGap } = visibility;
        const { errorOrSpacerHeight, toWinLabelHeight, keyboardTotalHeight, selectorToButtonGap } = heights;
        const { t } = useTranslation(['betslip_pickem']);

        const toWin = amount * boostedMultiplier;

        const navigation = useNavigation();
        const decimalsEnabled = useFantasyDecimalsEnabled({ gameMode: GameMode.RaceToPlace });

        const handleDepositPress = useCallback(() => {
            navigation.navigate('DepositStack', {
                screen: 'DepositScreen',
                params: { shouldRedirectToLobby: false, shouldPopToLobby: true },
            });
        }, [navigation]);

        const setAmount = useCallback(
            (value: number) => {
                setInputValue(String(value));
            },
            [setInputValue]
        );

        const hideKeyboard = useCallback(() => {
            setSelector('quick-amounts');
        }, [setSelector]);

        const showInputError = !!errorMessage || showDepositButton;

        const isAmountOutOfRange = amount === 0 || amount < minEntryAmount || amount > maxEntryAmount;

        return (
            <Box paddingHorizontal={'s16'}>
                {/* 1. Crossfade between QuickAmounts and Input */}
                <Crossfade
                    showFirst={selector === 'quick-amounts'}
                    firstHeight={QUICK_AMOUNTS_HEIGHT}
                    secondHeight={INPUT_HEIGHT}
                    heightDuration={HEIGHT_DURATION}
                    first={
                        <QuickAmountsButtons
                            onEditPress={() => setSelector('number-pad')}
                            onAmountPress={quickAmount => setAmount(quickAmount)}
                            amount={amount}
                        />
                    }
                    second={
                        <AmountInput
                            showError={showInputError}
                            initialFocusState={true}
                            amountInputLabel={t('entry', { maxEntryAmount: toLocaleCurrency(maxEntryAmount, true) })}
                            toWin={toWin}
                            amount={displayValue === '0' ? '' : displayValue}
                            showKeyboardOnFocus={false}
                            style={styles.input}
                            alwaysHideClearIcon
                            inputMode={'none'}
                            // autofocus on web causes issues with the modal height
                            // (it tries to scroll the content)
                            autoFocus={!isWeb}
                        />
                    }
                />

                {/* 2. Labels - error OR toWin */}
                <FadeInCollapse
                    visible={showErrorOrSpacer}
                    height={errorOrSpacerHeight}
                    opacityDuration={HEIGHT_DURATION}
                >
                    <Box pt={'s8'} pb={selector === 'number-pad' ? 's12' : 's16'}>
                        <Text textAlign="center" variant="labelLarge" color={ds.colorToken.text.danger}>
                            {errorMessage}
                        </Text>
                    </Box>
                </FadeInCollapse>

                <FadeInCollapse visible={showToWin} height={toWinLabelHeight} opacityDuration={HEIGHT_DURATION}>
                    <Box pt={'s16'} pb={'s16'}>
                        <Row alignItems={'center'} gap={'s10'} height={20}>
                            <LineSeparator flex={1} />
                            <Text
                                testID={'race-to-place-to-win-label'}
                                variant={'labelLarge'}
                                color={ds.colorToken.text.secondary}
                            >{`$${amount} to win ${formatBalance(toWin)}`}</Text>
                            <LineSeparator flex={1} />
                        </Row>
                    </Box>
                </FadeInCollapse>

                {/* 3. Keyboard */}
                <FadeInCollapse
                    visible={selector === 'number-pad'}
                    height={keyboardTotalHeight}
                    opacityDuration={HEIGHT_DURATION}
                >
                    <Box pb={'s16'}>
                        <NumberPad
                            hideBackSpace={displayValue === '0'}
                            onBackspacePress={onBackspacePress}
                            onDecimalPress={onDecimalPress}
                            onNumberPress={onNumberPress}
                            decimalsEnabled={decimalsEnabled}
                        />
                    </Box>
                </FadeInCollapse>

                {/* 4. Gap between selector and button (quick-amounts, no labels) */}
                <FadeInCollapse visible={showSelectorToButtonGap} height={selectorToButtonGap} />

                {/* 5. Place Entry / Deposit Button */}
                <Row width="100%" gap="s12">
                    <Box flex={1}>
                        {showDepositButton ? (
                            <Button
                                label={'Deposit to Fantasy'}
                                onPress={handleDepositPress}
                                hierarchy="primary"
                                textStyle={textVariants.titleMedium}
                                testID={'deposit-to-fantasy-button'}
                            />
                        ) : (
                            <Button
                                style={styles.placeEntryButton}
                                label="Place Entry"
                                onPress={onPlaceEntry}
                                hierarchy="primaryLilac"
                                loading={isLoading}
                                loaderColors={placeEntryLoaderColors}
                                disabled={isAmountOutOfRange}
                                disableContainerStyle={styles.placeEntryDisabledStyle}
                                textStyle={textVariants.titleMedium}
                                testID={'place-entry-button'}
                            />
                        )}
                    </Box>
                    {selector === 'number-pad' ? (
                        <Button
                            style={styles.hideKeyboardButton}
                            label={<ChevronDown />}
                            onPress={hideKeyboard}
                            hierarchy="secondary"
                            textStyle={textVariants.titleMedium}
                        />
                    ) : null}
                </Row>
            </Box>
        );
    }
);

const placeEntryLoaderColors = { main: ds.colorValue.icon.primary, second: 'transparent' };

const styles = StyleSheet.create({
    input: {
        marginVertical: 0,
    },
    placeEntryButton: {
        paddingVertical: 10,
    },
    placeEntryDisabledStyle: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    hideKeyboardButton: {
        paddingVertical: 10,
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
});
